import { ArrowRight, Building2, Plus } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { useApp } from "../context/AppContext";

const roleLabels: Record<string, string> = {
  OWNER: "Propriétaire",
  ADMIN: "Administrateur",
  MANAGER: "Gérant",
  PROJECT_MANAGER: "Chef de projet",
  ACCOUNTANT: "Comptable",
  MEMBER: "Membre",
  VIEWER: "Lecture seule"
};

export function EnterprisesPage() {
  const { bootstrap, switchOrganisation } = useApp();
  const navigate = useNavigate();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState("");
  const organisations = bootstrap?.organisations || [];
  const current = bootstrap?.active_organisation?.code || "";

  async function select(code: string) {
    if (busy || current === code) return;
    setBusy(code);
    setError("");
    try {
      await switchOrganisation(code);
      navigate("/", { replace: true });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Impossible d'ouvrir cette entreprise.");
    } finally {
      setBusy(null);
    }
  }

  return <main className="min-h-screen bg-slate-50 px-5 py-12">
    <div className="mx-auto max-w-5xl">
      <div className="flex flex-wrap items-center justify-between gap-5">
        <div>
          <img src="/saheltech-mark.svg" alt="SahelTech" className="h-12 w-auto" />
          <h1 className="mt-6 text-3xl font-black text-slate-950">Mes entreprises</h1>
          <p className="mt-2 text-sm text-slate-600">
            Un seul compte, plusieurs entreprises. Chaque espace garde ses propres données, rôles et abonnements.
          </p>
        </div>
        {bootstrap?.identity_federated && <Link to="/create-business" className="btn-primary flex items-center gap-2">
          <Plus size={17} /> Créer une entreprise
        </Link>}
      </div>
      {error && <p role="alert" className="mt-6 rounded-xl bg-amber-50 p-4 text-sm text-amber-900">{error}</p>}
      <div className="mt-8 grid gap-4 md:grid-cols-2">
        {organisations.map((org) => {
          const active = org.code === current;
          const role = roleLabels[(org.user_role || "").toUpperCase()] || org.user_role || "Membre";
          return <article key={org.code} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex items-start gap-3">
              <span className="rounded-xl bg-emerald-50 p-3 text-emerald-700"><Building2 size={23} /></span>
              <div className="min-w-0 flex-1">
                <h2 className="text-lg font-black text-slate-950">{org.nom}</h2>
                <p className="mt-1 text-sm text-slate-600">{role} · {org.pays} · {org.devise}</p>
                {active && <p className="mt-2 text-xs font-semibold text-emerald-700">Entreprise active</p>}
              </div>
            </div>
            <button className="btn-secondary mt-6 flex w-full items-center justify-center gap-2" disabled={active || busy !== null}
              onClick={() => void select(org.code)}>
              {active ? "Espace ouvert" : busy === org.code ? "Ouverture…" : "Accéder à cette entreprise"}
              {!active && <ArrowRight size={17} />}
            </button>
          </article>;
        })}
      </div>
      <p className="mt-7 text-sm text-slate-500">
        L'accès à une entreprise dépend toujours d'une adhésion active et des permissions vérifiées côté serveur.
      </p>
    </div>
  </main>;
}
